import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaSpinner, FaWallet } from "react-icons/fa";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { getCategoriesWithDetails } from "../../utils/transactionCategories";

const EditBudget = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
  });

  // Pobieramy kategorie wydatków
  const expenseCategories = getCategoriesWithDetails("expense");

  // Pobieranie danych budżetu
  useEffect(() => {
    const fetchBudget = async () => {
      if (!currentUser || !id) return;

      try {
        setLoading(true);
        const budgetDoc = await getDoc(doc(db, "budgets", id));

        if (!budgetDoc.exists()) {
          setError("Budżet nie został znaleziony.");
          return;
        }

        const budgetData = budgetDoc.data();
        if (budgetData.userId !== currentUser.uid) {
          setError("Nie masz uprawnień do edycji tego budżetu.");
          return;
        }

        setFormData({
          name: budgetData.name,
          amount: budgetData.amount.toString(),
        });
      } catch (error) {
        console.error("Błąd podczas pobierania budżetu:", error);
        setError("Nie udało się pobrać danych budżetu.");
      } finally {
        setLoading(false);
      }
    };

    fetchBudget();
  }, [currentUser, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    if (!formData.name) {
      setError("Nazwa budżetu jest wymagana.");
      return;
    }

    if (
      !formData.amount ||
      isNaN(Number(formData.amount)) ||
      Number(formData.amount) <= 0
    ) {
      setError("Kwota budżetu musi być liczbą większą od zera.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const budgetRef = doc(db, "budgets", id);
      await updateDoc(budgetRef, {
        name: formData.name,
        amount: Number(formData.amount),
        updatedAt: new Date(),
      });

      navigate("/budget");
    } catch (error) {
      console.error("Błąd podczas aktualizacji budżetu:", error);
      setError(
        "Nie udało się zaktualizować budżetu. Spróbuj ponownie później."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-green-400" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate("/budget")}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Powrót do budżetu
          </button>
          <h1 className="text-2xl font-semibold text-white flex items-center">
            <FaWallet className="text-green-400 mr-3" />
            Edytuj budżet
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 text-red-200 rounded-lg border border-red-700">
            {error}
          </div>
        )}

        <div className="bg-gray-800/30 rounded-lg border border-gray-700/40 overflow-hidden shadow-xl">
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Nazwa (kategoria)
                </label>
                <select
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  required
                >
                  <option value="">Wybierz kategorię...</option>
                  {expenseCategories.map((category) => (
                    <option key={category.name} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="amount"
                  className="block text-sm font-medium text-gray-300 mb-1"
                >
                  Kwota budżetu (PLN)
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-green-400 to-cyan-500 rounded-lg text-white shadow-lg hover:from-green-500 hover:to-cyan-600 transition-all duration-300 flex items-center"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Zapisywanie...
                  </>
                ) : (
                  <>
                    <FaSave className="mr-2" />
                    Zapisz zmiany
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditBudget;
